// drawOla cloud sync: React bridge. Fork-only.
//
// Subscribes to the engine and hands the UI a small API. `version` changes
// on every engine event, so a component that renders status re-renders.

import { useCallback, useEffect, useMemo, useState } from "react";

import type { DocMeta } from "../../storage/types";

import type { CloudSyncEngine, SyncStatus } from "./CloudSyncEngine";

export type CloudSyncApi = {
  engine: CloudSyncEngine;
  /** Bumps on every engine change; anything memoised on status must depend on it. */
  version: number;
  statusOf: (meta: DocMeta) => SyncStatus;
  providerName: (meta: DocMeta) => string;
  /** The draw whose repair panel is open, if any. */
  repairDocId: string | null;
  openRepair: (docId: string) => void;
  closeRepair: () => void;
};

/** Bridge one engine instance to React. `null` engine means cloud sync is off. */
export function useCloudSync(
  engine: CloudSyncEngine | null,
): CloudSyncApi | null {
  const [version, setVersion] = useState(0);
  const [repairDocId, setRepairDocId] = useState<string | null>(null);

  useEffect(() => {
    if (!engine) {
      return undefined;
    }
    return engine.subscribe(() => setVersion((v) => v + 1));
  }, [engine]);

  const openRepair = useCallback((docId: string) => setRepairDocId(docId), []);
  const closeRepair = useCallback(() => setRepairDocId(null), []);

  return useMemo(() => {
    if (!engine) {
      return null;
    }
    return {
      engine,
      version,
      statusOf: (meta: DocMeta) => engine.statusOf(meta),
      providerName: (meta: DocMeta) =>
        meta.cloud ? engine.providerName(meta.cloud.providerId) : "",
      repairDocId,
      openRepair,
      closeRepair,
    };
  }, [engine, version, repairDocId, openRepair, closeRepair]);
}
